import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export type OpeningHourEntry = {
  day: number;
  start: string | null;
  end: string | null;
};

const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export function normalizeOpeningTime(time: string): string | null {
  const match = time.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

export function getWeekdayInTimeZone(instant: Date, timeZone: string): number {
  const isoDay = Number(formatInTimeZone(instant, timeZone, 'i'));
  return isoDay === 7 ? 0 : isoDay;
}

export function getOpeningHoursSchedule(openingHours: OpeningHourEntry[]): OpeningHourEntry[] {
  return WEEKDAY_ORDER.map((day) => {
    const entry = openingHours.find((item) => item.day === day);
    return {
      day,
      start: entry?.start ?? null,
      end: entry?.end ?? null,
    };
  });
}

function buildOpeningInstant(instant: Date, time: string, timeZone: string): Date | null {
  const normalized = normalizeOpeningTime(time);
  if (!normalized) return null;

  const dateKey = formatInTimeZone(instant, timeZone, 'yyyy-MM-dd');
  return fromZonedTime(`${dateKey}T${normalized}:00`, timeZone);
}

export function isProposalOutsideOpeningHours(options: {
  start: Date;
  timeZone: string;
  openingHours?: OpeningHourEntry[] | null;
}): boolean {
  const { start, timeZone, openingHours } = options;
  if (openingHours == null) return false;

  const weekday = getWeekdayInTimeZone(start, timeZone);
  const entry = openingHours.find((item) => item.day === weekday);

  if (!entry?.start || !entry?.end) {
    return true;
  }

  const openStart = buildOpeningInstant(start, entry.start, timeZone);
  const openEndRaw = buildOpeningInstant(start, entry.end, timeZone);

  if (!openStart || !openEndRaw) {
    return false;
  }

  const openEnd =
    openEndRaw.getTime() <= openStart.getTime() ? new Date(openEndRaw.getTime() + 24 * 60 * 60 * 1000) : openEndRaw;
  const lastStart = new Date(openEnd.getTime() - 60 * 60 * 1000);

  return start.getTime() < openStart.getTime() || start.getTime() > lastStart.getTime();
}
